import { ReactNode } from 'react';
import { useTranslate } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';

import { Iconify } from 'src/components/iconify';
import { useResponsive } from 'src/hooks/use-responsive';
import { PermissionButton } from 'src/routes/components/permission-button';

import { fieldUnits, formatValue } from '../params/helpers';
import { formatInstant, formatUsdcAmount } from '../vaults/helpers';
import { VaultAddress } from '../vaults/VaultAddress';
import {
  FAUCET_PERMISSION_RESOURCE,
  OFFICIAL_VAULTS_NS as NS,
  OFFICIAL_VAULTS_PERMISSION_RESOURCE as RESOURCE,
  PROVISIONING_STEPS,
} from './constants';
import {
  ApplicationView,
  canApprove,
  canReject,
  canSendFaucet,
  FaucetBank,
  FIELD_LABELS,
  requesterLabel,
  canRedrive,
  canRetry,
  sizeCapFor,
  stepLabelKey,
  stakeTransferKey,
  vaultAddressHint,
} from './helpers';
import { alsoReceived, stakeToSend } from './stakeAmounts';
import { FaucetTransfers } from './FaucetTransfers';
import { KindChip, StatusChip } from './StatusChip';

const EMPTY = '—';
const ID = { fontFamily: 'monospace', fontSize: 12 } as const;

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <Stack spacing={1.5}>
    <Typography variant="subtitle2">{title}</Typography>
    {children}
  </Stack>
);

const Rows = ({ rows }: { rows: [string, ReactNode, boolean?][] }) => (
  <Box
    component="dl"
    sx={{ m: 0, display: 'grid', gap: 1, gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr)' }}
  >
    {rows.map(([label, value, mono]) => (
      <Box key={label} sx={{ display: 'contents' }}>
        <Typography component="dt" variant="body2" sx={{ color: 'text.secondary' }}>
          {label}
        </Typography>
        <Typography
          component="dd"
          variant="body2"
          sx={{ m: 0, overflowWrap: 'anywhere', ...(mono ? ID : {}) }}
        >
          {value}
        </Typography>
      </Box>
    ))}
  </Box>
);

/**
 * Details of one application (PO, 2026-10-01; STORY-22 T-07) and its actions: approve, reject, retry, and the testnet
 * faucet (XBIT-4348). There is no "Open for raising": approve is the only way a vault opens (AC15).
 */
export const DetailsDrawer = ({
  request,
  onClose,
  onRetry,
  onRedrive,
  onApprove,
  onReject,
  bank,
  onFaucet,
}: {
  request: ApplicationView | null;
  onClose: () => void;
  /** XBIT-4321: resubmit a FAILED request's terms as a new request. */
  onRetry: (request: ApplicationView) => void;
  /** XBIT-4240: resume a FAILED official request from the step that failed (same id, same terms). */
  onRedrive: (request: ApplicationView) => void;
  /** STORY-22 T-07: approve (opens the vault ACTIVE) and reject (closes the DRAFT row), behind vault:official-vaults review. */
  onApprove: (request: ApplicationView) => void;
  onReject: (request: ApplicationView) => void;
  /** XBIT-4348: the faucet bank; the action shows only for an offered bank. */
  bank: FaucetBank;
  /** XBIT-4348: send testnet USDC to a user through this vault (opens the faucet dialog). */
  onFaucet: (vaultId: string) => void;
}) => {
  const translate = useTranslate();
  const smDown = useResponsive('down', 'sm');
  const units = fieldUnits(translate);
  const t = (key: string) => translate(`${NS}.details.${key}`);

  // Where the manager sends the stake, and what to do with it by status (f1 review, 2026-10-02).
  const vaultAddressCell = (r: ApplicationView) => {
    const { hint, muted } = vaultAddressHint(r);
    // XBIT-4400: the hint names the amounts: what to send (stake + 1 before ACTIVATED) and what arrives.
    const toSend = stakeToSend(r);
    return (
      <Stack component="span" spacing={0.25}>
        <VaultAddress address={r.hlAddress} full muted={muted} />
        {hint && (
          <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
            {translate(`${NS}.details.vaultAddressHint.${hint}`, {
              total: toSend ? formatUsdcAmount(toSend) : EMPTY,
              stake: r.stake ? formatUsdcAmount(r.stake.expected) : EMPTY,
            })}
          </Typography>
        )}
      </Stack>
    );
  };

  const body = (r: ApplicationView) => {
    const reached = PROVISIONING_STEPS.indexOf(r.status);
    const sizeCap = r.stake ? sizeCapFor(r.stake.expected, r.ownershipFloor) : null;
    return (
      <Stack spacing={3} divider={<Divider />}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ overflowWrap: 'anywhere' }}>
              {r.name}
            </Typography>
            <KindChip kind={r.kind} />
            <StatusChip request={r} />
          </Stack>
          <Typography variant="caption" sx={{ ...ID, color: 'text.secondary' }}>
            {r.vaultId}
          </Typography>
        </Stack>

        {r.status === 'FAILED' && r.failure && (
          <Alert severity="error">
            {translate(`${NS}.table.failure`, { step: r.failure.step, reason: r.failure.reason })}
          </Alert>
        )}

        <Section title={t('provisioning')}>
          {reached >= 0 ? (
            <Stepper
              activeStep={reached + 1}
              alternativeLabel={!smDown}
              orientation={smDown ? 'vertical' : 'horizontal'}
            >
              {PROVISIONING_STEPS.map((step) => (
                <Step key={step}>
                  <StepLabel>{translate(stepLabelKey(step) ?? step)}</StepLabel>
                </Step>
              ))}
            </Stepper>
          ) : (
            // FAILED, or a step this build does not know yet: name it rather than guess a position.
            <Typography variant="body2">
              {t('currentStep')}:{' '}
              {stepLabelKey(r.status) ? translate(stepLabelKey(r.status)!) : r.status}
            </Typography>
          )}
        </Section>

        <Section title={t('strategy')}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {r.strategy}
          </Typography>
        </Section>

        <Section title={t('bindings')}>
          <Rows
            rows={[
              [translate(FIELD_LABELS.xHandle), r.xHandle || EMPTY],
              [translate(FIELD_LABELS.groupChatId), r.groupChatId || EMPTY, true],
            ]}
          />
        </Section>

        <Section title={t('manager')}>
          <Rows
            rows={[
              [translate(FIELD_LABELS.managerName), r.manager?.name ?? EMPTY, false],
              [translate(FIELD_LABELS.managerHolderId), r.manager?.holderId ?? EMPTY, true],
              [translate(FIELD_LABELS.managerAddress), r.manager?.address ?? EMPTY, true],
            ]}
          />
        </Section>

        <Section title={t('stake')}>
          <Rows
            rows={[
              [t('vaultAddress'), vaultAddressCell(r)],
              [t('stakeExpected'), r.stake ? `${formatUsdcAmount(r.stake.expected)} USDC` : EMPTY],
              [
                t('stakeReceived'),
                r.stake?.received ? `${formatUsdcAmount(r.stake.received)} USDC` : EMPTY,
              ],
              // XBIT-4400 (QA via f1): the manager's other parked transfers, beside the stake.
              ...(() => {
                const more = alsoReceived(r);
                return more
                  ? [
                      [
                        t('alsoReceived'),
                        translate(`${NS}.details.alsoReceivedValue`, {
                          count: more.count,
                          total: formatUsdcAmount(more.total),
                        }),
                      ] as [string, string],
                    ]
                  : [];
              })(),
              [
                t('stakeTransferStatus'),
                (() => {
                  const status = r.stake?.transferStatus;
                  const key = stakeTransferKey(r, status);
                  if (!key) return EMPTY;
                  // A status added later shows raw rather than untranslated keys.
                  const text = translate(key, { _: status ?? EMPTY });
                  return text === key ? (status ?? EMPTY) : text;
                })(),
              ],
            ]}
          />
        </Section>

        <Section title={t('terms')}>
          <Rows
            rows={[
              [translate(FIELD_LABELS.feePercent), formatValue('percent', r.feeRate, units)],
              [translate(FIELD_LABELS.lockupDays), formatValue('days', r.lockupDays, units)],
              [
                translate(FIELD_LABELS.ownershipFloor),
                formatValue('percent', r.ownershipFloor, units),
              ],
              [
                translate(FIELD_LABELS.sizeCap),
                sizeCap === null ? EMPTY : `${formatUsdcAmount(sizeCap)} USDC`,
              ],
            ]}
          />
        </Section>

        {(r.review || r.cancelledReason || r.status === 'CANCELLED') && (
          <Section title={t('review')}>
            {r.cancelledReason ? (
              <Alert severity="info">{r.cancelledReason}</Alert>
            ) : (
              // A cancellation the engine gave no reason for; the window is a stage parameter, so no number here.
              r.status === 'CANCELLED' &&
              r.review?.outcome !== 'REJECTED' && (
                <Alert severity="info">{t('cancelledFallback')}</Alert>
              )
            )}
            {r.review && (
              <Rows
                rows={[
                  [t('reviewOutcome'), translate(`${NS}.review.outcome.${r.review.outcome}`)],
                  [t('reviewActor'), r.review.actor, true],
                  [t('reviewAt'), formatInstant(r.review.at)],
                  ...(r.review.outcome === 'APPROVED'
                    ? // An approval before Option B carried a raise window; a new one has none (D-30 2026-10-02).
                      ([
                        ...(r.review.raisingStartedAt
                          ? [
                              [
                                t('reviewRaisingStartedAt'),
                                formatInstant(r.review.raisingStartedAt),
                              ],
                            ]
                          : []),
                        ...(r.review.raisingInceptionAt
                          ? [
                              [
                                t('reviewRaisingInceptionAt'),
                                formatInstant(r.review.raisingInceptionAt),
                              ],
                            ]
                          : []),
                      ] as [string, ReactNode][])
                    : ([[translate(`${NS}.review.reason`), r.review.reason ?? EMPTY]] as [
                        string,
                        ReactNode,
                      ][])),
                ]}
              />
            )}
          </Section>
        )}

        {/* XBIT-4348: the faucet's requests, with a FAILED one's reason; only where the faucet is offered and the operator may read it. */}
        {bank.offered && (
          <PermissionButton resource={FAUCET_PERMISSION_RESOURCE} action="read">
            <FaucetTransfers vaultId={r.vaultId} />
          </PermissionButton>
        )}

        <Section title={t('audit')}>
          <Rows
            rows={[
              [translate(`${NS}.table.requestedBy`), requesterLabel(r), !r.requestedByName],
              [translate(`${NS}.table.requestedAt`), formatInstant(r.requestedAt)],
              [t('updatedAt'), formatInstant(r.updatedAt)],
              // XBIT-4680 (C-06 commitmentAcceptedAt): empty for OFFICIAL requests, older records and applications that did not send it.
              [t('commitmentAcceptedAt'), r.commitmentAcceptedAt ? formatInstant(r.commitmentAcceptedAt) : EMPTY],
            ]}
          />
        </Section>
      </Stack>
    );
  };

  /** One action bar: reject on the left, the forward action (approve or retry) on the right. */
  const actions = (r: ApplicationView) => {
    const reject = canReject(r);
    const approve = canApprove(r);
    const retry = canRetry(r);
    const redrive = canRedrive(r);
    const faucet = bank.offered && canSendFaucet(r);
    if (!reject && !approve && !retry && !redrive && !faucet) return null;
    return (
      <>
        <Divider />
        <Stack direction="row" spacing={1} sx={{ p: 2, justifyContent: 'flex-end' }}>
          {reject && (
            <PermissionButton resource={RESOURCE} action="review">
              <Button variant="outlined" color="error" onClick={() => onReject(r)}>
                {translate(
                  `${NS}.review.${
                    r.review?.outcome === 'REJECTED'
                      ? 'rejectAgain'
                      : r.review?.outcome === 'APPROVED'
                        ? 'rejectInstead'
                        : 'reject'
                  }`
                )}
              </Button>
            </PermissionButton>
          )}
          {faucet && (
            <PermissionButton resource={FAUCET_PERMISSION_RESOURCE} action="create">
              <Button variant="outlined" onClick={() => onFaucet(r.vaultId)}>
                {translate(`${NS}.faucet.action`)}
              </Button>
            </PermissionButton>
          )}
          {approve && (
            <PermissionButton resource={RESOURCE} action="review">
              <Button variant="contained" onClick={() => onApprove(r)}>
                {translate(
                  `${NS}.review.${r.review?.outcome === 'APPROVED' ? 'approveAgain' : 'approve'}`
                )}
              </Button>
            </PermissionButton>
          )}
          {redrive && (
            // The same id and terms, from the step that failed: its own permission (update), apart from Retry's.
            <PermissionButton resource={RESOURCE} action="update">
              <Button variant="outlined" onClick={() => onRedrive(r)}>
                {translate(`${NS}.redrive.action`)}
              </Button>
            </PermissionButton>
          )}
          {retry && (
            // A retry is a new request: the same permission as Create official vault.
            <PermissionButton resource={RESOURCE} action="create">
              <Button variant="contained" onClick={() => onRetry(r)}>
                {t('retry')}
              </Button>
            </PermissionButton>
          )}
        </Stack>
      </>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={Boolean(request)}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}
    >
      <Stack direction="row" sx={{ px: 2.5, py: 2, alignItems: 'center' }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {t('title')}
        </Typography>
        <IconButton onClick={onClose} aria-label={t('close')}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      </Stack>
      <Divider />
      <Box sx={{ p: 2.5, overflowY: 'auto', flexGrow: 1 }}>{request && body(request)}</Box>
      {request && actions(request)}
    </Drawer>
  );
};
